import React from 'react';

import { CircularProgress, Stack, useTheme } from '@mui/material';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { Edit2, IconProps, TickSquare } from 'iconsax-reactjs';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { updateUser } from '../../../../api/user';
import TabPanel from '../../../../components/TabPanel';
import { IUser } from '../../../../types/user.type';
import { SectionWrapper } from '../../styles';
import CvTab from './components/CvTab';
import ProfessionalTab from './components/ProfessionalTab';
import UserFormTabs from './components/UserFormTabs';
import { createInitialValues } from './form.factory';
import { EditButtonStyled } from './styles';
import { IUserForm, IUserFormTab } from './types';
import { validationSchema } from './validation';

const FORM_ID = 'user_form';

interface IProps {
  user: IUser;
  onSave: (data: IUser) => void;
}

const UserForm = React.memo(({ user, onSave }: IProps) => {
  const [isEditable, setIsEditable] = React.useState<boolean>(false);
  const [isProcess, setIsProcess] = React.useState<boolean>(false);
  const [view, setView] = React.useState<IUserFormTab>(IUserFormTab.professional);

  const { t } = useTranslation();

  const theme = useTheme();

  const editIconProps: IconProps = {
    size: 18,
    color: theme.palette.neutral.main,
  };

  const handleEditableChange = React.useCallback(() => {
    setIsEditable(!isEditable);
  }, [isEditable]);

  const handleSubmit = React.useCallback(
    async (values: IUserForm, helpers: FormikHelpers<IUserForm>) => {
      setIsProcess(true);

      try {
        const response = await updateUser(values);

        onSave(response);

        toast.success(t('profile.success_update'));

        helpers.setFieldValue('cv', '');
        helpers.setFieldValue('cv_fr', '');

        setIsEditable(false);
      } catch {
        toast.error(t('profile.error_update'));
      } finally {
        setIsProcess(false);
      }
    },
    [onSave, t],
  );

  const formik = useFormik<IUserForm>({
    enableReinitialize: true,
    initialValues: createInitialValues(user),
    validationSchema: validationSchema(t),
    onSubmit: handleSubmit,
  });

  React.useEffect(() => {
    if (formik.submitCount > 0 && !formik.isValid) {
      toast.error(t('profile.error_validation'));
    }
  }, [formik.submitCount, formik.isValid, t]);

  return (
    <SectionWrapper>
      <Stack direction="row" justifyContent="space-between">
        <UserFormTabs view={view} onChange={setView} />

        {isEditable && (
          <EditButtonStyled form={FORM_ID} type="submit" color="primary" disabled={isProcess}>
            {isProcess ? (
              <CircularProgress size={editIconProps.size} color="neutral" />
            ) : (
              <TickSquare {...editIconProps} />
            )}
          </EditButtonStyled>
        )}

        {!isEditable && (
          <EditButtonStyled onClick={handleEditableChange} color="primary">
            <Edit2 {...editIconProps} />
          </EditButtonStyled>
        )}
      </Stack>

      <Stack width="100%" sx={{ marginTop: 2 }}>
        <FormikProvider value={formik}>
          <form id={FORM_ID} onSubmit={formik.handleSubmit}>
            <TabPanel
              key={`user_form_tab_${IUserFormTab.professional}`}
              value={view}
              index={IUserFormTab.professional}
            >
              <ProfessionalTab isEditable={isEditable} />
            </TabPanel>

            <TabPanel key={`user_form_tab_${IUserFormTab.cv}`} value={view} index={IUserFormTab.cv}>
              <CvTab user={user} onDelete={onSave} isEditable={isEditable} />
            </TabPanel>
          </form>
        </FormikProvider>
      </Stack>
    </SectionWrapper>
  );
});

export default UserForm;
